import React, { useMemo, useState, SetStateAction } from 'react';
import type { Risk } from '../types/risk.type';
import RiskHeatmap from './RiskHeatmap';
import RiskSummary from './RiskSummary';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';

export default function RiskOverview({
    risks,
    onSelectCategory,
}: {
    risks: Risk[];
    onSelectCategory: React.Dispatch<SetStateAction<string | null>>;
}) {
    const [isCollapsed, setIsCollapsed] = useState(false);

    const ragTotals = useMemo(() => {
        const R = risks.filter((r) => r.rag === 'R').length;
        const A = risks.filter((r) => r.rag === 'A').length;
        const G = risks.filter((r) => r.rag === 'G').length;
        return { R, A, G, all: risks.length };
    }, [risks]);

    return (
        <div className="w-full rounded-2xl border bg-white p-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium">Risk Overview</h3>
                    <button
                        type="button"
                        onClick={() => setIsCollapsed((v) => !v)}
                        className="p-1 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200"
                        aria-expanded={!isCollapsed}
                        aria-controls="risk-heatmap-body"
                        title={isCollapsed ? '펼치기' : '접기'}
                    >
                        {isCollapsed ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronUpIcon className="w-4 h-4" />}
                    </button>
                </div>

                <div className="text-xs text-gray-600 space-x-4">
                    <span className="inline-flex items-center gap-1">
                        <i className="w-2 h-2 rounded-full bg-rose-500" />
                        R: <b>{ragTotals.R}</b>
                    </span>
                    <span className="inline-flex items-center gap-1">
                        <i className="w-2 h-2 rounded-full bg-amber-500" />
                        A: <b>{ragTotals.A}</b>
                    </span>
                    <span className="inline-flex items-center gap-1">
                        <i className="w-2 h-2 rounded-full bg-emerald-500" />
                        G: <b>{ragTotals.G}</b>
                    </span>
                    <span className="ml-2 text-gray-400">Total: {ragTotals.all}</span>
                </div>
            </div>

            {!isCollapsed && (
                <div className="mt-5 flex items-center gap-12">
                    <div className="w-[30%]">
                        <RiskHeatmap risks={risks} />
                    </div>
                    <div className="flex-1">
                        <RiskSummary risks={risks} onSelectCategory={onSelectCategory} />
                    </div>
                </div>
            )}
        </div>
    );
}
